import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';

import { captureSentryException } from 'shared/utils';

import type { ReValidationMode, ValidationMode } from '../types';

interface UseZodFormOptions<T extends z.ZodType> {
  /** Zod schema used for form validation */
  schema: T;
  /**
   * Default values for the form fields.
   * Must provide initial values for all form fields to ensure controlled inputs.
   */
  defaultValues: z.infer<T>;
  /** Callback for form submission, receives validated form values. */
  onSubmit: (values: z.infer<T>) => void | Promise<void>;
  /**
   * Optional form validation mode, determines when validation is triggered.
   * @default 'onBlur'
   */
  validationMode?: ValidationMode;
  /**
   * Optional re-validation mode, determines when validation re-runs after the first submit.
   * Use `'onSubmit'` when the form clears server-set field errors itself: the default re-runs
   * the resolver on every keystroke, which replaces a just-cleared message with its own.
   * @default 'onChange'
   */
  reValidationMode?: ReValidationMode;
  /** Optional callback for handling submission errors, receives error object. */
  onError?: (error: unknown) => void;
}

/**
 * useZodForm
 * Wrapper for useForm with zodResolver, providing a clean, type-safe API for forms.
 * - Uses zodResolver for schema validation
 * - Requires explicit default values for all fields
 * - Supports validation and re-validation modes (onBlur, onChange, onTouched, onSubmit)
 * - Supports form submission with error handling
 */
export const useZodForm = <T extends z.ZodType>({
  schema,
  defaultValues,
  validationMode = 'onBlur',
  reValidationMode = 'onChange',
  onSubmit,
  onError
}: UseZodFormOptions<T>) => {
  const form = useForm<z.infer<T>>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: validationMode,
    reValidateMode: reValidationMode,
    criteriaMode: 'all', // Show all errors
    shouldFocusError: true // Focus first error on submit
  });

  // TODO: improve in next iterations, when we'll have logic for BE
  const handleFormSubmit = form.handleSubmit(async (values) => {
    try {
      await onSubmit(values);
    } catch (error) {
      if (onError) {
        onError(error);
      } else {
        captureSentryException({
          message: 'Error when submitting form to the BE',
          error,
          feature: 'shared',
          extra: {
            method: 'useZodForm - handleFormSubmit'
          }
        });
      }
    }
  });

  return {
    form,
    handleFormSubmit
  };
};
